import { CRYPTO_SWAP_NETWORKS } from "../constants/cryptoSwaps.constants";
import { keccak256, toHex, utf8Bytes } from "./hash/keccak256";

/**
 * Offline address verification: the only control that runs before an unrecoverable on-chain transfer. Pure
 * and synchronous so it can run inside a Zod refine and in the Node unit project.
 */

export type AddressCheckStatus = "empty" | "valid" | "invalid" | "unsupported_network";

export interface AddressCheck {
  status: AddressCheckStatus;
  ok: boolean;
}

const EVM_SHAPE = /^0x[0-9a-fA-F]{40}$/;

/**
 * EIP-55. An all-lower or all-upper address is valid but unchecksummed (plenty of wallets export it); only a
 * mixed-case address whose capitalisation disagrees with the hash is a typo we can prove.
 */
export function isValidEvmAddress(address: string): boolean {
  if (!EVM_SHAPE.test(address)) return false;

  const body = address.slice(2);
  if (body === body.toLowerCase() || body === body.toUpperCase()) return true;

  const hash = toHex(keccak256(utf8Bytes(body.toLowerCase())));
  for (let i = 0; i < body.length; i++) {
    if (body[i] >= "0" && body[i] <= "9") continue;
    const shouldBeUpper = parseInt(hash[i], 16) >= 8;
    const isUpper = body[i] >= "A" && body[i] <= "F";
    if (shouldBeUpper !== isUpper) return false;
  }
  return true;
}

/** Fails closed on a network with no row in `CRYPTO_SWAP_NETWORKS`: we cannot verify its addresses. */
export function checkCryptoSwapAddress(network: string, rawAddress: string): AddressCheck {
  const address = rawAddress.trim();
  if (!isSupportedNetwork(network)) return { status: "unsupported_network", ok: false };
  if (address.length === 0) return { status: "empty", ok: false };
  return isValidEvmAddress(address) ? { status: "valid", ok: true } : { status: "invalid", ok: false };
}

export function isSupportedNetwork(network: string | null | undefined): boolean {
  return !!network && network in CRYPTO_SWAP_NETWORKS;
}

const PREFERRED_NETWORK = "POLYGON";

/** The network a form starts on: Polygon whenever the pair offers it, else the catalog's first. */
export function defaultNetwork(networks: readonly string[]): string {
  return networks.includes(PREFERRED_NETWORK) ? PREFERRED_NETWORK : (networks[0] ?? "");
}

/** First and last six characters (what people actually compare against a wallet) plus the middle in groups. */
export function chunkAddress(
  address: string,
  edge = 6,
  group = 4,
): { head: string; middle: string[]; tail: string } {
  const value = address.trim();
  if (value.length <= edge * 2) return { head: value, middle: [], tail: "" };

  const head = value.slice(0, edge);
  const tail = value.slice(-edge);
  const middle = value.slice(edge, -edge);
  const groups: string[] = [];
  for (let i = 0; i < middle.length; i += group) groups.push(middle.slice(i, i + group));
  return { head, middle: groups, tail };
}

export function shortenAddress(address: string, edge = 6): string {
  const value = address.trim();
  return value.length <= edge * 2 + 1 ? value : `${value.slice(0, edge)}…${value.slice(-edge)}`;
}

/** The name a wallet saved without one gets: its address as people compare it, in the user's language. */
export function defaultWalletAlias(
  address: string,
  format: (ends: { start: string; end: string }) => string,
): string {
  return format({ start: address.slice(0, 6), end: address.slice(-4) });
}
